import {
  roundOverlayEl,
  matchResultsEl,
  overlayKickerEl,
  overlayWinnerNameEl,
  overlayWinnerTailEl,
  overlayLabelEl,
  overlayCountdownEl
} from './dom.js';
import { normalizeTeam, teamAccent, teamLabel } from '/shared/teams.js';
import { lobby, model, ui } from '../engine/state.js';
import { serverNowMs } from '../engine/time.js';
import { setTextIfChanged } from '../utils/utils.js';
import { playerColorById, playerLabelById } from '../engine/players.js';
import { standingsSignature, remainingSeconds } from '../engine/state-helpers.js';
import { INTERROUND_COUNTDOWN_MS } from '../engine/config.js';

const MATCH_END_OVERLAY_DELAY_MS = 700;

function renderMatchResults(standings, winnerId, isTie) {
  if (!matchResultsEl) return;
  if (!Array.isArray(standings) || standings.length === 0) {
    matchResultsEl.replaceChildren();
    return;
  }
  const heading = document.createElement('div');
  heading.className = 'match-results-heading';
  const headingLabel = document.createElement('span');
  headingLabel.textContent = 'Final standings';
  const headingScore = document.createElement('span');
  headingScore.textContent = 'Score';
  heading.appendChild(headingLabel);
  heading.appendChild(headingScore);

  const rows = [heading];
  const boardRankById = new Map(
    (Array.isArray(lobby.matchBoardRanks) ? lobby.matchBoardRanks : [])
      .map((entry) => [Number(entry.id), Number(entry.rank)])
  );
  const topScore = Number(standings[0]?.score) || 0;
  let displayedPosition = 1;
  let previousScore = null;
  for (const [index, entry] of standings.entries()) {
    const id = Number(entry?.id);
    const score = Number(entry?.score) || 0;
    if (index > 0 && score !== previousScore) displayedPosition = index + 1;
    previousScore = score;
    const row = document.createElement('div');
    row.className = 'match-row';
    const isTiedLeader = isTie && score === topScore;
    if ((!isTie && id === winnerId) || isTiedLeader) row.classList.add('is-winner');
    if (isTiedLeader) row.classList.add('is-tied');

    const left = document.createElement('div');
    left.className = 'match-row-name';
    const position = document.createElement('span');
    position.className = 'match-row-position';
    position.textContent = String(displayedPosition);
    const dot = document.createElement('span');
    dot.className = 'dot';
    dot.style.background = playerColorById(id);
    const name = document.createElement('span');
    name.className = 'match-row-name-text';
    name.textContent = playerLabelById(id, { capitalizeYou: true });
    left.appendChild(position);
    left.appendChild(dot);
    left.appendChild(name);

    const right = document.createElement('div');
    right.className = 'match-row-right';
    // Where this result landed on the leaderboard. Bots never have one (they
    // are not on the board), and nobody does when the match went unrecorded.
    const boardRank = boardRankById.get(id);
    if (boardRank) {
      const board = document.createElement('span');
      board.className = 'match-row-board';
      board.title = 'Leaderboard placing';
      const icon = document.createElement('i');
      // icons.css carries a hand-picked subset; bi-trophy-fill is in it, the
      // outline bi-trophy is not and renders as a missing glyph box.
      icon.className = 'bi bi-trophy-fill';
      icon.setAttribute('aria-hidden', 'true');
      const boardText = document.createElement('span');
      boardText.textContent = `#${boardRank}`;
      board.append(icon, boardText);
      right.appendChild(board);
    }
    const rank = document.createElement('span');
    rank.className = 'match-row-rank';
    rank.textContent = String(score);
    right.appendChild(rank);

    row.appendChild(left);
    row.appendChild(right);
    rows.push(row);
  }
  matchResultsEl.replaceChildren(...rows);
}

function renderRoundOverlay(nowMs = serverNowMs()) {
  if (!roundOverlayEl) return;
  const status = model.state?.status || {};

  if (status.gameOver) {
    if (!ui.matchEndOverlayReady) {
      if (ui.matchEndOverlayShowAt <= 0) {
        ui.matchEndOverlayShowAt = nowMs + MATCH_END_OVERLAY_DELAY_MS;
      }
      if (nowMs < ui.matchEndOverlayShowAt) {
        roundOverlayEl.classList.remove('is-visible', 'is-match-end');
        if (matchResultsEl) {
          matchResultsEl.replaceChildren();
        }
        ui.standingsSig = '0';
        return;
      }
      ui.matchEndOverlayReady = true;
    }
    ui.waitingRoundKey = '';
    ui.waitingOverlayNeedsAnimate = false;
    const winnerId = Number(status.matchWinnerId);
    const isTie = Boolean(status.matchTie);
    // A team match is won by a SIDE. The top scorer can be on the losing team,
    // so naming them here would announce the wrong winner.
    const winnerTeam = normalizeTeam(status.matchWinnerTeam);
    setTextIfChanged(overlayWinnerNameEl, isTie
      ? "It's a tie!"
      : (winnerTeam ? teamLabel(winnerTeam) : playerLabelById(winnerId, { capitalizeYou: true })));
    setTextIfChanged(overlayKickerEl, isTie ? 'Match complete' : 'Match winner');
    setTextIfChanged(overlayWinnerTailEl, isTie ? '' : 'wins the match!');
    if (overlayWinnerNameEl) {
      overlayWinnerNameEl.style.color = isTie
        ? ''
        : (winnerTeam ? teamAccent(winnerTeam) : playerColorById(winnerId));
    }
    const hasRestartCountdown = Number(status.matchRestartAt) > 0;
    setTextIfChanged(overlayLabelEl, hasRestartCountdown ? 'Restart in' : 'Match complete');
    setTextIfChanged(overlayCountdownEl, hasRestartCountdown ? remainingSeconds(status.matchRestartAt, nowMs) : '');
    roundOverlayEl.classList.add('is-visible', 'is-match-end');
    const standings = Array.isArray(model.state?.standings) ? model.state.standings : [];
    // Placings land a moment after the standings do, so they are part of what
    // makes the card stale.
    const boardSig = (Array.isArray(lobby.matchBoardRanks) ? lobby.matchBoardRanks : [])
      .map((entry) => `${entry.id}#${entry.rank}`)
      .join(',');
    const signature = `${isTie ? 1 : 0}:${winnerId}:${winnerTeam || ''}:${standingsSignature(standings)}:${boardSig}`;
    if (ui.standingsSig !== signature) {
      ui.standingsSig = signature;
      renderMatchResults(standings, winnerId, isTie);
    }
    return;
  }

  ui.matchEndOverlayShowAt = 0;
  ui.matchEndOverlayReady = false;

  if (status.waitingNext && Number(status.nextRoundAt) > 0) {
    const roundWinnerId = Number(status.roundWinnerId);
    const roundKey = `${roundWinnerId}:${status.nextRoundAt}`;
    if (ui.waitingRoundKey !== roundKey) {
      ui.waitingRoundKey = roundKey;
      ui.waitingOverlayNeedsAnimate = true;
      roundOverlayEl.classList.remove('is-visible', 'is-match-end');
      if (matchResultsEl) {
        matchResultsEl.replaceChildren();
      }
    }

    const showAt = Number(status.nextRoundAt) - INTERROUND_COUNTDOWN_MS;
    if (!Number.isFinite(showAt) || nowMs < showAt) {
      roundOverlayEl.classList.remove('is-visible', 'is-match-end');
      if (matchResultsEl) {
        matchResultsEl.replaceChildren();
      }
      ui.standingsSig = '0';
      return;
    }

    if (ui.waitingOverlayNeedsAnimate) {
      void roundOverlayEl.offsetWidth;
      ui.waitingOverlayNeedsAnimate = false;
    }

    setTextIfChanged(overlayWinnerNameEl, playerLabelById(roundWinnerId, { capitalizeYou: true }));
    setTextIfChanged(overlayKickerEl, 'Round complete');
    setTextIfChanged(overlayWinnerTailEl, 'wins this round!');
    if (overlayWinnerNameEl) {
      overlayWinnerNameEl.style.color = playerColorById(roundWinnerId);
    }
    setTextIfChanged(overlayLabelEl, 'Next round in');
    setTextIfChanged(overlayCountdownEl, Math.max(1, remainingSeconds(status.nextRoundAt, nowMs)));
    roundOverlayEl.classList.remove('is-match-end');
    roundOverlayEl.classList.add('is-visible');
    if (matchResultsEl) {
      matchResultsEl.replaceChildren();
    }
    ui.standingsSig = '0';
    return;
  }

  ui.waitingRoundKey = '';
  ui.waitingOverlayNeedsAnimate = false;
  roundOverlayEl.classList.remove('is-visible', 'is-match-end');
  if (matchResultsEl) {
    matchResultsEl.replaceChildren();
  }
  ui.standingsSig = '0';
}

function updateDelayedMatchEndOverlay(nowMs = serverNowMs()) {
  const status = model.state?.status || {};
  if (!status.gameOver || ui.matchEndOverlayReady || ui.matchEndOverlayShowAt <= 0) return;
  if (nowMs >= ui.matchEndOverlayShowAt) {
    ui.overlayDirty = true;
  }
}

function updateOverlayCountdown(nowMs = serverNowMs()) {
  if (!overlayCountdownEl) return;
  const status = model.state?.status || {};
  let targetAt = 0;
  if (status.gameOver && Number(status.matchRestartAt) > 0) {
    targetAt = Number(status.matchRestartAt);
  } else if (status.waitingNext && Number(status.nextRoundAt) > 0) {
    targetAt = Number(status.nextRoundAt);
  }
  if (targetAt <= 0) {
    ui.lastOverlayCountdown = -1;
    return;
  }
  const secs = status.waitingNext
    ? Math.max(1, remainingSeconds(targetAt, nowMs))
    : remainingSeconds(targetAt, nowMs);
  if (secs !== ui.lastOverlayCountdown) {
    ui.lastOverlayCountdown = secs;
    setTextIfChanged(overlayCountdownEl, secs);
  }
}

export {
  renderRoundOverlay,
  updateDelayedMatchEndOverlay,
  updateOverlayCountdown
};
